import React, { ChangeEvent, useCallback, useState } from 'react';
import { Button, Col, Form, Input, message, Modal, Popconfirm, Row, Switch } from 'antd';
import { Link } from 'react-router-dom';
import { ERoutesPrivate } from 'common/models/routesModel';
import { useFormMapper } from 'common/helpers/form.helper';
import { useIsContentRestricted } from 'common/hooks/useIsContentRestricted';
import { useAppSelector } from 'app/store/store.hooks';
import { EMenuPageTabKeys } from 'app/pages/Franchisor/MenuPage';
import { IAssetCreateData, IAssetMenuItem, IAssetModel, IAssetUpdateData } from 'entities/Asset/Asset.models';
import { AssetSortableParent } from 'entities/Asset/components/DnD/AssetSortableParent';
import { ECommonEntityStatus } from 'entities/Bitecoin/Bitecoin.models';

interface IComponentProps {
  createAsset?: (data: IAssetCreateData) => void;
  updateAsset?: (data: IAssetUpdateData) => void;
  deleteAsset?: (id: string) => void;
  assetId?: string;
}

type AllProps = IComponentProps;

const AssetFormComponent: React.FC<AllProps> = (props) => {
  const { assetModel, assetModelLoading, assetModelParams, assetModelError } = useAppSelector((state) => state.asset);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [freeItems, setFreeItems] = useState<IAssetMenuItem[] | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [onlyAddedToMenu, setOnlyAddedToMenu] = useState(true);
  const [onlyAddedToBuilder, setOnlyAddedToBuilder] = useState(false);
  const [form] = Form.useForm();
  const { assetId, createAsset, updateAsset, deleteAsset } = props;
  const isUpdateForm = !!assetId;
  const { fields } = useFormMapper(['name', 'categoriesToAsset'], assetModel, assetModelParams, assetModelError);
  const submitButtonText = isUpdateForm ? 'Save' : 'Create asset';
  const canManageAsset = !useIsContentRestricted();
  const isFieldDisabled = assetModelLoading || !canManageAsset;

  const onFinish = (values: any) => {
    let isEmptyPriceExists = false;
    const freeItems: IAssetMenuItem[] = [];

    // Handle creating a new asset
    if (!isUpdateForm) {
      addAsset(values);

      return;
    }

    // Handle editing the asset
    (values as IAssetModel).categoriesToAsset.map((category) => {
      category.menuItemsToAsset.map((menuItem) => {
        if (isNaN(menuItem.price)) {
          isEmptyPriceExists = true;
        }

        if (menuItem.status === ECommonEntityStatus.Active && menuItem.price === 0 && menuItem.isInMenu) {
          freeItems.push(menuItem);
        }
      });
    });

    if (isEmptyPriceExists) {
      message.error('Please fill in all the prices');

      return;
    }

    if (freeItems.length) {
      setFreeItems(freeItems);
      setIsModalVisible(true);

      return;
    }

    editAsset(values);
  };

  const onCancel = () => setIsModalVisible(false);

  const onConfirm = () => {
    const values = form.getFieldsValue(true);

    isUpdateForm ? editAsset(values) : addAsset(values);
  };

  const addAsset = (values: any) => {
    const { name } = values;
    const vals: IAssetCreateData = {
      name,
    };

    createAsset && createAsset(vals);
  };

  const editAsset = (values: any) => {
    const { name, categoriesToAsset } = values;

    if (assetId) {
      const vals: IAssetUpdateData = {
        name,
        id: assetId,
        categoriesToAsset,
      };

      updateAsset && updateAsset(vals);
    }
  };

  const removeAsset = () => {
    if (assetId) {
      deleteAsset && deleteAsset(assetId);
    }
  };

  const autocompleteSearch = useCallback((text: string) => {
    setFilter(text.trim().toLowerCase());
  }, []);

  const onAddedToMenuChange = (checked: boolean) => {
    setOnlyAddedToMenu(checked);
  };

  const onAddedToBuilderChange = (checked: boolean) => {
    setOnlyAddedToBuilder(checked);
  };

  return (
    <>
      <Modal
        width={640}
        open={isModalVisible}
        footer={null}
        onCancel={() => setIsModalVisible(false)}
        title="Confirm Free Items in your asset"
      >
        <div className="mb-4">The following items are about to be added for FREE in your asset</div>
        <div className="mb-6">
          {freeItems &&
            freeItems.map((freeItem) => {
              return (
                <div key={freeItem.id} className="mb-2">
                  • {freeItem.name}
                </div>
              );
            })}
        </div>
        <div className="mb-6">Please confirm that this is accurate before changes can be applied.</div>
        <div className="flex flex__column flex-align-center">
          <Button
            className="popup__btn mb-4"
            disabled={assetModelLoading}
            loading={assetModelLoading}
            title="Confirm"
            onClick={onConfirm}
          >
            Confirm
          </Button>
          <Button
            className="popup__btn"
            type="primary"
            disabled={assetModelLoading}
            loading={assetModelLoading}
            title="Cancel"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      </Modal>
      <Form onFinish={onFinish} fields={fields} form={form} layout="vertical">
        <Form.Item
          rules={[
            {
              required: true,
              message: 'Asset name is required',
            },
            {
              min: 2,
              message: 'Asset name must be longer than or equal 2 characters',
            },
            {
              max: 100,
              message: 'Asset name must be shorter than or equal 100 characters',
            },
          ]}
          name="name"
          label="Asset name"
        >
          <Input name="assetName" type="text" placeholder="Asset Name" disabled={isFieldDisabled} />
        </Form.Item>
        {isUpdateForm && (
          <>
            <Row className="mb-6 width-full" justify="space-between" align="middle">
              <Col span={14}>
                <Row justify="start" align="bottom">
                  <Col span={20} className="mr-4">
                    <Input.Search
                      placeholder="Search by product or category name"
                      onChange={(e: ChangeEvent<HTMLInputElement>) => autocompleteSearch(e.target.value)}
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={5}>
                <Row justify="end">
                  <Switch checked={onlyAddedToMenu} onChange={onAddedToMenuChange} />
                  <div className="ml-2">Added to menu</div>
                </Row>
              </Col>
              <Col span={5}>
                <Row justify="end">
                  <Switch checked={onlyAddedToBuilder} onChange={onAddedToBuilderChange} />
                  <div className="ml-2">Added to builder</div>
                </Row>
              </Col>
            </Row>

            <Form.Item name="categoriesToAsset">
              <AssetSortableParent
                filter={filter}
                onlyAddedToMenu={onlyAddedToMenu}
                onlyAddedToBuilder={onlyAddedToBuilder}
                disabled={isFieldDisabled}
              />
            </Form.Item>
          </>
        )}
        <Row justify="space-between" align="middle">
          <Col span={16}>
            <Form.Item className="mb-0">
              <Button
                className="mr-8"
                htmlType="submit"
                type="primary"
                disabled={assetModelLoading || !canManageAsset}
                loading={assetModelLoading}
                title={submitButtonText}
              >
                {submitButtonText}
              </Button>
              <Link to={`${ERoutesPrivate.Menu}?tab=${EMenuPageTabKeys.Assets}`}>
                <Button disabled={assetModelLoading} loading={assetModelLoading} title="Cancel">
                  Cancel
                </Button>
              </Link>
            </Form.Item>
          </Col>
          {isUpdateForm && (
            <Popconfirm
              disabled={assetModelLoading || !canManageAsset}
              title="Are you sure you want to delete the asset?"
              okType="danger"
              onConfirm={removeAsset}
              okText="Delete"
            >
              <Button danger disabled={assetModelLoading || !canManageAsset} loading={assetModelLoading} title="Delete">
                Delete
              </Button>
            </Popconfirm>
          )}
        </Row>
      </Form>
    </>
  );
};

export const AssetForm = AssetFormComponent;
